/**
 * Converts a File or Blob to a base64-encoded data URL string.
 *
 * @param file - The File or Blob object to convert
 * @param callback - Callback function that receives the base64 string
 *
 * @example
 * const file = new File(['content'], 'image.png', { type: 'image/png' });
 * getBase64FromFile(file, (base64) => {
 *   console.log(base64); // "data:image/png;base64,..."
 * });
 */
export const getBase64FromFile = (
  file: File | Blob,
  callback: (url: string) => void,
): void => {
  const reader = new FileReader()
  reader.addEventListener("load", () => callback(reader.result as string))
  reader.readAsDataURL(file)
}

/**
 * Converts a base64 data URL string to a File object.
 *
 * @param dataUrl - The base64 data URL string (format: "data:image/png;base64,...")
 * @param filename - The name to give the resulting File object
 * @returns A File object containing the decoded image data
 *
 * @example
 * const canvas = document.createElement('canvas');
 * const dataUrl = canvas.toDataURL('image/png');
 * const file = dataURLtoFile(dataUrl, 'image.png');
 */
export const dataURLtoFile = (dataUrl: string, filename: string): File => {
  const blob = dataURLtoBlob(dataUrl)
  return new File([blob], filename, { type: blob.type })
}

/**
 * Converts a base64 data URL string to a Blob object.
 *
 * @param dataUrl - The base64 data URL string (format: "data:image/png;base64,...")
 * @returns A Blob object containing the decoded image data
 *
 * @example
 * const canvas = document.createElement('canvas');
 * const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
 * const blob = dataURLtoBlob(dataUrl);
 */
export const dataURLtoBlob = (dataUrl: string): Blob => {
  // Split the data URL to extract the MIME type and base64 data
  const parts = dataUrl.split(",")
  const mimeMatch = parts[0].match(/:(.*?);/)

  if (!mimeMatch || parts.length !== 2) {
    throw new Error("Invalid data URL format")
  }

  const mimeType = mimeMatch[1]
  const base64Data = parts[1]

  // Decode the base64 string
  const binaryString = atob(base64Data)

  // Convert binary string to Uint8Array
  const length = binaryString.length
  const uint8Array = new Uint8Array(length)

  for (let i = 0; i < length; i++) {
    uint8Array[i] = binaryString.charCodeAt(i)
  }

  // Create and return the Blob
  return new Blob([uint8Array], { type: mimeType })
}

/**
 * Detects if the current browser is Safari version 17 or higher.
 * Safari 17+ has native HEIC/HEIF support, so no conversion is needed.
 *
 * @returns true if Safari 17+, false otherwise
 *
 * @example
 * if (isSafari17OrHigher()) {
 *   // Use native HEIC support
 * } else {
 *   // Convert HEIC to JPEG
 * }
 */
export const isSafari17OrHigher = (): boolean => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

  const safariVersion = navigator.userAgent.match(/Version\/(\d+)/)

  return isSafari && safariVersion ? parseInt(safariVersion[1]) >= 17 : false
}

/**
 * Checks if a file is HEIC or HEIF format.
 *
 * @param file - The file to check
 * @returns true if the file is HEIC or HEIF format
 *
 * @example
 * const file = new File(['content'], 'photo.heic', { type: 'image/heic' });
 * if (isHeicOrHeifFile(file)) {
 *   // Handle HEIC/HEIF conversion
 * }
 */
export const isHeicOrHeifFile = (file: File): boolean => {
  return file.type === "image/heic" || file.type === "image/heif"
}

/**
 * Handles image file changes with automatic HEIC/HEIF conversion for non-Safari 17 browsers.
 * Creates a handler function that processes images and calls appropriate callbacks.
 *
 * @param options - Configuration options
 * @param options.onProcessImage - Callback for processing HEIC/HEIF files (conversion required)
 * @param options.onUploadImage - Callback for uploading base64-encoded images (direct upload)
 * @returns A function that handles image file changes
 *
 * @example
 * const handleImageChange = createImageChangeHandler({
 *   onProcessImage: (file) => processImage(file),
 *   onUploadImage: (base64, file) => uploadImage(base64, file),
 * });
 *
 * // Use with file input
 * <input type="file" onChange={(e) => handleImageChange(e.target.files?.[0])} />
 */
export const createImageChangeHandler = (options: {
  onProcessImage: (file: File) => void
  onUploadImage: (base64: string, file: File) => void
  isForceConvertHeic?: boolean
}) => {
  return (file?: File) => {
    if (!file) return

    const shouldConvert = isHeicOrHeifFile(file) && !isSafari17OrHigher()

    if (shouldConvert) {
      options.onProcessImage(file)
    } else {
      getBase64FromFile(file, (base64) => {
        options.onUploadImage(base64, file)
      })
    }
  }
}

/**
 * Loads an image from a URL, draws it to a canvas, and converts it to a JPEG File.
 *
 * @param url - The URL of the image to convert (e.g. an object URL or remote URL)
 * @returns A Promise resolving to a File named "barcode-image.jpg" with type "image/jpeg"
 *
 * @example
 * const file = await convertImageUrlToFile(imageUrl)
 */
export const convertImageUrlToFile = (url: string): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")

      if (!ctx) {
        reject(new Error("Failed to get canvas context"))
        return
      }

      ctx.drawImage(img, 0, 0)

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], "barcode-image.jpg", {
              type: "image/jpeg",
            })
            resolve(file)
          } else {
            reject(new Error("Failed to create blob from canvas"))
          }
        },
        "image/jpeg",
        0.95,
      )
    }

    img.onerror = () => {
      reject(new Error("Failed to load image"))
    }

    img.crossOrigin = "anonymous"
    img.src = url
  })
}
